import { Check } from 'lucide-react';
import { AnimatePresence, motion, useDragControls } from 'motion/react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { cn } from '../lib/cn';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
};

const SPRING = { type: 'spring', stiffness: 420, damping: 38, mass: 0.9 } as const;

/**
 * Native-style modal. On phones a bottom sheet with a grab handle that
 * follows the finger and closes on a downward fling; on wider screens a
 * small centered card. Escape and the scrim close it, focus stays
 * inside while open and returns to the trigger afterwards.
 */
export function Sheet({ open, onClose, title, children, className }: Props) {
  const isPhone = !useMediaQuery('(min-width: 768px)');
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useDragControls();
  useFocusTrap(panelRef, open);

  useEffect(() => {
    if (!open) return;
    const back = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      back?.focus?.();
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6">
          <motion.button
            type="button"
            tabIndex={-1}
            aria-label="Закрыть"
            onClick={onClose}
            className="absolute inset-0 cursor-default bg-[oklch(0%_0_0/0.32)] backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            ref={panelRef}
            // biome-ignore lint/a11y/useSemanticElements: animated portal sheet; <dialog> top layer fights motion
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={cn(
              'relative w-full bg-surface shadow-2xl outline-none',
              'mc-safe-bottom rounded-t-[28px] pb-2 md:max-w-[400px] md:rounded-[24px] md:pb-0',
              className,
            )}
            initial={isPhone ? { y: '100%' } : { opacity: 0, scale: 0.96, y: 8 }}
            animate={isPhone ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={isPhone ? { y: '100%' } : { opacity: 0, scale: 0.97, y: 4 }}
            transition={SPRING}
            drag={isPhone ? 'y' : false}
            dragListener={false}
            dragControls={drag}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.9 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 90 || info.velocity.y > 600) onClose();
            }}
          >
            <div
              className="touch-none px-5 pt-2 pb-1 md:pt-5"
              onPointerDown={(e) => isPhone && drag.start(e)}
            >
              <div
                aria-hidden="true"
                className="mx-auto mb-3 h-[5px] w-10 rounded-full bg-[oklch(var(--fg)/0.18)] md:hidden"
              />
              <h2 id={titleId} className="text-[17px] font-semibold tracking-[-0.01em]">
                {title}
              </h2>
            </div>
            <div className="max-h-[70dvh] overflow-y-auto overscroll-contain px-3 pt-2 pb-3">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/** Tappable row for a sheet: icon, label, optional hint, selected tick. */
export function SheetRow({
  icon,
  label,
  hint,
  selected,
  onClick,
  role = 'button',
  value,
}: {
  value?: string;
  icon?: ReactNode;
  label: string;
  hint?: string;
  selected?: boolean;
  onClick: () => void;
  role?: 'button' | 'menuitemradio';
}) {
  return (
    <button
      type="button"
      role={role === 'button' ? undefined : role}
      aria-checked={role === 'menuitemradio' ? !!selected : undefined}
      data-value={value}
      onClick={onClick}
      className={cn(
        'mc-press flex min-h-[52px] w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left',
        selected ? 'bg-subtle' : 'active:bg-subtle md:hover:bg-subtle',
      )}
    >
      {icon && <span className="shrink-0 text-muted-foreground">{icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] leading-tight">{label}</span>
        {hint && <span className="mt-0.5 block text-[13px] text-muted-foreground">{hint}</span>}
      </span>
      {selected && (
        <Check size={18} strokeWidth={2.4} aria-hidden="true" className="shrink-0 text-primary" />
      )}
    </button>
  );
}
